import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { TABLE_COLUMNS } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getCarsListSelector,
	getCarTypesSelector,
	getIsCarsListLoadingSelector,
	getPaginationSelector,
	getTableSearchSelector,
	getIsDownloadLoadingSelector,
} from './selectors';
import { getCarsListAction, getCarTypesAction, resetStateAction, getListCarFileAction } from './actions';
import { getIsAvailableCarsButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';

const CarsContainerWrapper = ({
	onGetCarsList,
	carsList,
	isCarsListLoading,
	onGetCarTypesList,
	carTypes,
	pagination,
	onResetState,
	isAvailable,
	onGetCarFile,
	isDownloadLoading,
}) => {
	useInjectSaga({ key: 'carsContainerSaga', saga });
	useInjectReducer({ key: 'carsContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetCarsList({ searchQuery: removeModalQuery(query.toString()) });
		onGetCarTypesList();

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'car');
		query.set('mode', 'add');
		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetCarsList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetCarsList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleDownloadButtonClick = () => {
		onGetCarFile({ exportUrl: '/api/cars/export' });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetCarsList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS(carTypes)));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetCarsList({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<>
			<DataGridComponent
				headerText="Машины"
				onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
				onDownloadButtonClick={isAvailable.export ? handleDownloadButtonClick : null}
				columns={TABLE_COLUMNS(carTypes)}
				isDownloadButtonLoading={isDownloadLoading}
				rows={carsList}
				pagination={
					!pagination
						? null
						: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
				}
				isTableDataLoading={isCarsListLoading}
				handleSortChange={handleSortChange}
				handleFilterChange={handleFilterChange}
			/>
		</>
	);
};

const mapStateToProps = createStructuredSelector({
	carsList: getCarsListSelector(),
	isCarsListLoading: getIsCarsListLoadingSelector(),
	isDownloadLoading: getIsDownloadLoadingSelector(),
	carTypes: getCarTypesSelector(),
	pagination: getPaginationSelector(),
	tableSearch: getTableSearchSelector(),
	isAvailable: getIsAvailableCarsButtonsSelector(),
});

const mapDispatchToProps = {
	onGetCarsList: getCarsListAction,
	onGetCarFile: getListCarFileAction,
	onGetCarTypesList: getCarTypesAction,
	onResetState: resetStateAction,
};

const CarsContainer = connect(mapStateToProps, mapDispatchToProps)(CarsContainerWrapper);

CarsContainerWrapper.propTypes = {
	onGetCarFile: PropTypes.func.isRequired,
	onGetCarsList: PropTypes.func.isRequired,
	carsList: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
			number: PropTypes.string.isRequired,
			vin: PropTypes.string.isRequired,
			link: PropTypes.string.isRequired,
		}),
	),
	isCarsListLoading: PropTypes.bool.isRequired,
	isDownloadLoading: PropTypes.bool.isRequired,
	onGetCarTypesList: PropTypes.func.isRequired,
	carTypes: PropTypes.array,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

export default CarsContainer;
